/**
 * Centralized Signaling Server Configuration
 *
 * This file provides consistent signaling server configuration across
 * all components, with environment variable support and fallbacks.
 */

// Working signaling servers for reliable P2P connections
const DEFAULT_SIGNALING_SERVERS = [
  // These are the most reliable servers currently working
  'wss://demos.yjs.dev',
  
  // Add more working servers as fallbacks
  'wss://y-webrtc-ams1.fly.dev',
  'wss://y-webrtc-fra1.fly.dev',
  'wss://y-webrtc-sfo1.fly.dev',
  'wss://y-webrtc-sin1.fly.dev',
  'wss://y-webrtc-signaling-us.herokuapp.com'
]

// Track server status for intelligent routing
const serverStatus = new Map()

// Check if a server is available (with caching)
async function checkServerAvailability(serverUrl) {
  const cacheKey = `server_status_${serverUrl}`
  const cached = sessionStorage.getItem(cacheKey)
  const cacheTime = sessionStorage.getItem(`${cacheKey}_time`)
  
  // Use cached result if less than 5 minutes old
  if (cached && cacheTime && (Date.now() - parseInt(cacheTime)) < 300000) {
    return cached === 'true'
  }

  try {
    // Quick connectivity check
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 3000)
    
    const response = await fetch(serverUrl.replace('wss://', 'https://').replace('ws://', 'http://'), {
      method: 'HEAD',
      signal: controller.signal,
      mode: 'no-cors' // Avoid CORS issues
    })
    
    clearTimeout(timeoutId)
    const isAvailable = true // If we reach here, server is reachable
    
    // Cache the result
    sessionStorage.setItem(cacheKey, 'true')
    sessionStorage.setItem(`${cacheKey}_time`, Date.now().toString())
    
    return isAvailable
  } catch (error) {
    // Cache negative result for shorter time
    sessionStorage.setItem(cacheKey, 'false')
    sessionStorage.setItem(`${cacheKey}_time`, Date.now().toString())
    return false
  }
}

/**
 * Get signaling servers from environment variables or use defaults
 * Supports both comma-separated strings and arrays
 */
export function getSignalingServers() {
  // Try to get from environment variables (browser-safe)
  const envServers = import.meta.env?.VITE_YJS_SIGNALING_SERVERS ||
                    (typeof process !== 'undefined' ? process.env?.YJS_SIGNALING_SERVERS : null) ||
                    window?.ENV?.YJS_SIGNALING_SERVERS

  if (envServers) {
    if (typeof envServers === 'string') {
      return envServers.split(',').map(server => server.trim()).filter(Boolean)
    }
    if (Array.isArray(envServers)) {
      return envServers.filter(Boolean)
    }
  }

  console.log('📡 Using default signaling servers:', DEFAULT_SIGNALING_SERVERS)
  return DEFAULT_SIGNALING_SERVERS
}

/**
 * Get available signaling servers (smart selection)
 */
export async function getAvailableSignalingServers() {
  const allServers = getSignalingServers()
  const availableServers = []
  
  // Check servers in parallel
  const checks = allServers.map(async (server) => {
    try {
      const isAvailable = await checkServerAvailability(server)
      if (isAvailable) {
        availableServers.push(server)
      }
      return { server, available: isAvailable }
    } catch (error) {
      console.warn(`❌ Server check failed for ${server}:`, error.message)
      return { server, available: false }
    }
  })

  await Promise.all(checks)
  
  // If no servers are available, fallback to all servers (maybe connectivity check failed)
  if (availableServers.length === 0) {
    console.warn('⚠️ No signaling servers passed connectivity check, using all servers as fallback')
    return allServers
  }

  console.log('✅ Available signaling servers:', availableServers)
  return availableServers
}

/**
 * Get WebRTC provider configuration with optimized settings
 */
export function getWebRTCConfig(maxConns = 20) {
  return {
    signaling: getSignalingServers(),
    maxConns,
    filterBcConns: true,
    maxBackoffTime: 10000, // Increased backoff time
    backoffFactor: 2,
    retryDelay: 1000,
    // Enhanced peer configuration for cross-network connectivity
    peerOpts: {
      config: {
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' },
          { urls: 'stun:stun2.l.google.com:19302' },
          { urls: 'stun:stun3.l.google.com:19302' },
          { urls: 'stun:stun4.l.google.com:19302' },
          // Additional STUN servers for better connectivity
          { urls: 'stun:stun.services.mozilla.com' },
          { urls: 'stun:stun.cloudflare.com:3478' }
        ],
        iceCandidatePoolSize: 10
      },
      // Additional connection options
      optional: [
        { DtlsSrtpKeyAgreement: true },
        { RtpDataChannels: true }
      ]
    }
  }
}

/**
 * Get WebRTC configuration with smart server selection
 */
export async function getSmartWebRTCConfig(maxConns = 20) {
  const availableServers = await getAvailableSignalingServers()
  return {
    signaling: availableServers,
    maxConns,
    filterBcConns: true,
    maxBackoffTime: 10000,
    backoffFactor: 2,
    retryDelay: 1000,
    peerOpts: {
      config: {
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' },
          { urls: 'stun:stun2.l.google.com:19302' },
          { urls: 'stun:stun3.l.google.com:19302' },
          { urls: 'stun:stun4.l.google.com:19302' },
          { urls: 'stun:stun.services.mozilla.com' },
          { urls: 'stun:stun.cloudflare.com:3478' }
        ],
        iceCandidatePoolSize: 10
      },
      optional: [
        { DtlsSrtpKeyAgreement: true },
        { RtpDataChannels: true }
      ]
    }
  }
}

/**
 * Log signaling server status for debugging
 */
export function logSignalingStatus() {
  const servers = getSignalingServers()
  console.log('🔧 Signaling Configuration:', {
    servers,
    count: servers.length,
    primary: servers[0],
    fallbacks: servers.slice(1)
  })
  return servers
}

export default {
  getSignalingServers,
  getAvailableSignalingServers,
  getWebRTCConfig,
  getSmartWebRTCConfig,
  logSignalingStatus,
  DEFAULT_SIGNALING_SERVERS,
  checkServerAvailability
}